import { useCallback, useEffect, useRef, useState } from "react";
import { api, parseApiError } from "@/lib/api";
import { PageHeader } from "./AdminLayout";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ChefHat, Store, Briefcase } from "lucide-react";
import { toast } from "sonner";
import { validatePosCheckout } from "@/lib/business-validation";
import { clearDraft, loadDraft, roundPrice, saveDraft } from "@/lib/pos-utils";
import { usePosHotkeys } from "@/lib/use-pos-hotkeys";
import { SkuGrid } from "@/components/pos/sku-grid";
import { Cart } from "@/components/pos/cart";
import { PaymentDialog } from "@/components/pos/payment-dialog";
import { ReceiptDialog } from "@/components/pos/receipt-dialog";
import { RecentOrders } from "@/components/pos/recent-orders";
import { OrderDetailDialog } from "@/components/pos/order-detail-dialog";
import { ShiftBar } from "@/components/pos/shift-bar";
import { OfflineBanner } from "@/components/pos/offline-banner";
import { BootError } from "@/components/pos/boot-error";
import { CartActions } from "@/components/pos/cart-actions";

const CHANNELS = [
  { v: "retail", l: "FMCG Retail", icon: Store },
  { v: "restaurant", l: "Nhà hàng", icon: ChefHat },
  { v: "b2b", l: "Đại lý B2B", icon: Briefcase },
];

export default function POS() {
  const [channel, setChannel] = useState("retail");
  const [skus, setSkus] = useState(null);
  const [branches, setBranches] = useState([]);
  const [customers, setCustomers] = useState([]);
  // branchId persists across channel switches — FMCG/Restaurant/B2B at one site share a branch.
  // Per #31 AC5 decision: switching channel only resets ephemeral entry state (table/customer/search).
  const [branchId, setBranchId] = useState("");
  const [customerId, setCustomerId] = useState("");
  const [tableNo, setTableNo] = useState("");
  const [cart, setCart] = useState([]);
  const [q, setQ] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [receipt, setReceipt] = useState(null);
  const [detailId, setDetailId] = useState(null);
  const [ordersRefresh, setOrdersRefresh] = useState(0);
  const [bootError, setBootError] = useState(null);
  const searchRef = useRef(null);

  const loadMasters = useCallback(() => {
    setBootError(null);
    Promise.all([
      api.get("/master/skus").then((r) => setSkus(r.data)),
      api.get("/branches").then((r) => { setBranches(r.data); if (r.data[0] && !branchId) setBranchId(r.data[0].id); }),
      api.get("/master/customers").then((r) => setCustomers(r.data)),
    ]).catch((e) => { const m = parseApiError(e, "Không tải được dữ liệu POS").message; setBootError(m); toast.error(m); });
  }, [branchId]);
  useEffect(() => { loadMasters(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const subtotal = cart.reduce((a, l) => a + l.qty * l.unit_price, 0);

  const addToCart = useCallback((sku) => {
    if (Number(sku.stock) <= 0) { toast.warning(`${sku.name} đã hết hàng`); return; }
    const price = roundPrice(sku.price);
    setCart((c) => {
      const exist = c.find((x) => x.sku_id === sku.id);
      if (exist) return c.map((x) => x.sku_id === sku.id ? { ...x, qty: x.qty + 1 } : x);
      return [...c, { sku_id: sku.id, sku_code: sku.code, sku_name: sku.name, qty: 1, unit_price: price }];
    });
    setFieldErrors((e) => ({ ...e, lines: undefined }));
    searchRef.current?.focus();
  }, []);

  const updateQty = useCallback((id, d) => {
    setCart((c) => c.map((x) => x.sku_id === id ? { ...x, qty: Math.max(1, x.qty + d) } : x));
    setFieldErrors((e) => ({ ...e, lines: undefined }));
  }, []);

  const removeLine = useCallback((id) => {
    setCart((c) => c.filter((x) => x.sku_id !== id));
    setFieldErrors((e) => ({ ...e, lines: undefined }));
  }, []);

  const onChannelChange = (v) => {
    setChannel(v);
    setCart([]); setTableNo(""); setCustomerId(""); setQ(""); setFieldErrors({});
  };

  const onSaveDraft = () => {
    if (cart.length === 0) return toast.info("Giỏ trống — không có gì để lưu");
    if (saveDraft(channel, { cart, tableNo, customerId, branchId })) toast.success("Đã lưu nháp");
  };
  const onRestoreDraft = () => {
    const d = loadDraft(channel); if (!d) return;
    setCart(d.cart || []); setTableNo(d.tableNo || ""); setCustomerId(d.customerId || "");
    if (d.branchId) setBranchId(d.branchId);
    toast.success("Đã khôi phục nháp");
  };
  const hasDraft = Boolean(loadDraft(channel));

  const checkout = async (paymentInfo) => {
    const payload = {
      channel, branch_id: branchId,
      table_no: channel === "restaurant" ? tableNo : null,
      customer_id: channel === "b2b" ? customerId : null,
      lines: cart, ...paymentInfo,
    };
    const validation = validatePosCheckout(payload);
    if (!validation.ok) { setFieldErrors(validation.fieldErrors); toast.error(validation.message); return; }
    setSubmitting(true);
    try {
      if (paymentInfo.payments) {
        const create = await api.post("/pos/orders", { ...payload, payment_method: undefined, payments: undefined });
        await api.post(`/pos/orders/${create.data.id}/pay`, { payments: paymentInfo.payments });
        setReceipt(create.data);
      } else {
        const r = await api.post("/pos/orders", payload);
        setReceipt(r.data);
      }
      setCart([]); setTableNo(""); setFieldErrors({}); setPaymentOpen(false);
      clearDraft(channel);
      setOrdersRefresh((n) => n + 1);
    } catch (e) {
      const parsed = parseApiError(e, "Lỗi");
      setFieldErrors(parsed.fieldErrors);
      toast.error(parsed.message);
    } finally { setSubmitting(false); }
  };

  usePosHotkeys({
    focusSearch: () => searchRef.current?.focus(),
    openPayment: () => setPaymentOpen(true),
    clearCart: () => setCart([]),
    cartLength: cart.length,
  });

  const channelLabel = CHANNELS.find((c) => c.v === channel)?.l || channel;

  return (
    <div data-testid="page-pos">
      <PageHeader kicker="P2 • POS" title="Bán hàng đa kênh" breadcrumb={["Vận hành", "POS"]} />
      <div className="p-6 lg:p-8">
        <OfflineBanner />
        <BootError message={bootError} onRetry={loadMasters} />

        <div className="flex items-center justify-between mb-2 gap-3">
          <Tabs value={channel} onValueChange={onChannelChange} className="flex-1">
            <TabsList className="bg-stone-100 rounded-sm">
              {CHANNELS.map((c) => (
                <TabsTrigger key={c.v} value={c.v} className="rounded-sm data-[state=active]:bg-white data-[state=active]:text-emerald-900" data-testid={`tab-pos-${c.v}`}>
                  <c.icon className="w-4 h-4 mr-1.5" aria-hidden="true" />{c.l}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
          <ShiftBar branchId={branchId} />
        </div>

        <Tabs value={channel}>
          <TabsContent value={channel} forceMount>
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
              <SkuGrid
                ref={searchRef} skus={skus} loading={skus === null}
                q={q} onQueryChange={setQ}
                branchId={branchId} branches={branches}
                onBranchChange={(v) => { setBranchId(v); setFieldErrors((e) => ({ ...e, branch_id: undefined })); }}
                branchError={fieldErrors.branch_id}
                onAdd={addToCart}
                onScannerEnter={(sku) => { addToCart(sku); setQ(""); }}
              />
              <Cart
                channel={channel} cart={cart}
                tableNo={tableNo} onTableNoChange={(v) => { setTableNo(v); setFieldErrors((e) => ({ ...e, table_no: undefined })); }}
                customerId={customerId} customers={customers}
                onCustomerChange={(v) => { setCustomerId(v); setFieldErrors((e) => ({ ...e, customer_id: undefined })); }}
                fieldErrors={fieldErrors} onQtyChange={updateQty} onRemove={removeLine} subtotal={subtotal}
              >
                <CartActions cart={cart} hasDraft={hasDraft} onSaveDraft={onSaveDraft} onRestoreDraft={onRestoreDraft}
                  onOpenPayment={() => setPaymentOpen(true)} submitting={submitting} />
              </Cart>
            </div>

            <RecentOrders channel={channel} channelLabel={channelLabel}
              refreshToken={ordersRefresh} branches={branches}
              onOpenDetail={setDetailId} />
          </TabsContent>
        </Tabs>

        <PaymentDialog open={paymentOpen} onOpenChange={setPaymentOpen} channel={channel} subtotal={subtotal} onConfirm={checkout} submitting={submitting} />
        {receipt && <ReceiptDialog order={receipt} onClose={() => setReceipt(null)} onNewOrder={() => setReceipt(null)} />}
        {detailId && <OrderDetailDialog orderId={detailId} onClose={() => setDetailId(null)} onChanged={() => setOrdersRefresh((n) => n + 1)} />}
      </div>
    </div>
  );
}
